/**
 * Pure routing helpers shared between Vue (algoliaRouting.ts) and Blade (marketplace-search.ts).
 * No Vue / Inertia dependencies — safe to import in any bundle.
 */
import { history } from "instantsearch.js/es/lib/routers";
import type { UiState } from "instantsearch.js";
import qs from "qs";

// ─── Types ───────────────────────────────────────────────────
export type RangeAttribute =
    | "monthly_price"
    | "mileage"
    | "price"
    | "registration_at"
    | "weight"
    | "co2_emission"
    | "power";

export interface RangeValue {
    min?: number;
    max?: number;
}

export interface CuratedSearchFilters {
    monthly_price_min?: number;
    monthly_price_max?: number;
    mileage_min?: number;
    mileage_max?: number;
    price_min?: number;
    price_max?: number;
    year_min?: number;
    year_max?: number;
    weight_max?: number;
    co2_emission_min?: number;
    co2_emission_max?: number;
    power_min?: number;
    power_max?: number;
    refinementList?: Record<string, string[]>;
    menu?: Record<string, string>;
    has_promotion?: boolean;
}

// ─── Range meta table (source of truth) ─────────────────────
export interface RangeMeta {
    attribute: RangeAttribute;
    legacyName: string;
    labelKey: string;
    unit: string;
    minKey: keyof CuratedSearchFilters | null;
    maxKey: keyof CuratedSearchFilters;
    legacyMinKey?: string;
    legacyMaxKey?: string;
}

export const RANGE_META: RangeMeta[] = [
    {
        attribute: "monthly_price",
        legacyName: "monthlyPriceRange",
        labelKey: "vehicle.monthlyPrice",
        unit: "€",
        minKey: "monthly_price_min",
        maxKey: "monthly_price_max",
    },
    {
        attribute: "mileage",
        legacyName: "mileageRange",
        labelKey: "vehicle.mileage",
        unit: "km",
        minKey: "mileage_min",
        maxKey: "mileage_max",
    },
    {
        attribute: "price",
        legacyName: "priceRange",
        labelKey: "vehicle.price",
        unit: "€",
        minKey: "price_min",
        maxKey: "price_max",
    },
    {
        attribute: "registration_at",
        legacyName: "yearRange",
        labelKey: "vehicle.year",
        unit: "",
        minKey: "year_min",
        maxKey: "year_max",
        legacyMinKey: "registration_at_min",
        legacyMaxKey: "registration_at_max",
    },
    {
        attribute: "weight",
        legacyName: "weightRange",
        labelKey: "vehicle.weight",
        unit: "kg",
        minKey: null,
        maxKey: "weight_max",
    },
    {
        attribute: "co2_emission",
        legacyName: "co2Range",
        labelKey: "vehicle.co2",
        unit: "g/km",
        minKey: "co2_emission_min",
        maxKey: "co2_emission_max",
    },
    {
        attribute: "power",
        legacyName: "powerRange",
        labelKey: "vehicle.power",
        unit: "ch",
        minKey: "power_min",
        maxKey: "power_max",
    },
];

export const LEGACY_NAME_TO_ATTRIBUTE: Record<string, RangeAttribute> =
    Object.fromEntries(
        RANGE_META.map(({ legacyName, attribute }) => [legacyName, attribute]),
    );

// ─── Sort helpers ────────────────────────────────────────────
const SORT_SUFFIX_TO_FRIENDLY: Record<string, string> = {
    "monthly_price:asc": "monthly_price_asc",
    "monthly_price:desc": "monthly_price_desc",
    "mileage:asc": "mileage_asc",
    "mileage:desc": "mileage_desc",
    "power:asc": "power_asc",
    "power:desc": "power_desc",
    "registration_at:asc": "registration_at_asc",
    "registration_at:desc": "registration_at_desc",
};
const FRIENDLY_TO_SORT_SUFFIX: Record<string, string> = Object.fromEntries(
    Object.entries(SORT_SUFFIX_TO_FRIENDLY).map(([k, v]) => [v, k]),
);

const extractSortSuffix = (
    sortIndex: string,
    indexName: string,
): string | null => {
    const prefix = indexName + "/sort/";
    return sortIndex.startsWith(prefix) ? sortIndex.slice(prefix.length) : null;
};

export const sortIndexToFriendly = (
    sortIndex: string,
    indexName: string,
): string | null => {
    const suffix = extractSortSuffix(sortIndex, indexName);
    return suffix
        ? (SORT_SUFFIX_TO_FRIENDLY[suffix] ?? suffix.replace(":", "_"))
        : null;
};

export const friendlyToSortIndex = (
    friendly: string,
    indexName: string,
): string => {
    const suffix = FRIENDLY_TO_SORT_SUFFIX[friendly];
    return `${indexName}/sort/${
        suffix ?? friendly.replace(/_([^_]+)$/, ":$1")
    }`;
};

// ─── URL parsing ─────────────────────────────────────────────
export const parseUrlToRouteState = (
    urlString: string,
): Record<string, any> => {
    try {
        const url = new URL(urlString);
        return qs.parse(url.search.slice(1), { arrayLimit: 100 }) as Record<
            string,
            any
        >;
    } catch {
        return {};
    }
};

export const parseRouteStateFilters = (
    routeState: Record<string, any>,
): CuratedSearchFilters => {
    const toNum = (v: any) => (v !== undefined ? Number(v) : undefined);
    const result: Record<string, number | undefined> = {};
    for (const meta of RANGE_META) {
        if (meta.minKey) {
            result[meta.minKey] = toNum(
                routeState[meta.minKey] ??
                    (meta.legacyMinKey
                        ? routeState[meta.legacyMinKey]
                        : undefined),
            );
        }
        result[meta.maxKey] = toNum(
            routeState[meta.maxKey] ??
                (meta.legacyMaxKey ? routeState[meta.legacyMaxKey] : undefined),
        );
    }
    return result as CuratedSearchFilters;
};

// ─── Blade-specific routing ──────────────────────────────────
// Simplified routing for vanilla InstantSearch (no Vue reactivity, no Inertia guards).
// Range filters flow through InstantSearch's native `range` UI state.

function createBladeStateMapping(indexName: string) {
    return {
        stateToRoute(uiState: UiState): Record<string, any> {
            const activeIndexName =
                Object.keys(uiState).find(
                    (key) =>
                        key === indexName ||
                        key.startsWith(indexName + "/sort/"),
                ) ?? indexName;
            const indexUiState = uiState[activeIndexName] || {};
            const routeState: Record<string, any> = {};

            // Sort
            const sortByValue =
                (indexUiState as any).sortBy ??
                (activeIndexName !== indexName ? activeIndexName : undefined);
            if (sortByValue && sortByValue !== indexName) {
                const friendly = sortIndexToFriendly(sortByValue, indexName);
                if (friendly) routeState.sortBy = friendly;
            }

            // Query
            if (indexUiState.query) routeState.query = indexUiState.query;

            // Menu (make)
            if (
                indexUiState.menu &&
                Object.keys(indexUiState.menu).length > 0
            ) {
                routeState.menu = indexUiState.menu;
            }

            // RefinementList (model, energy, body_type, seats).
            // has_promotion is a flat URL flag — never nest it under refinementList.
            if (
                indexUiState.refinementList &&
                Object.keys(indexUiState.refinementList).length > 0
            ) {
                const { has_promotion: _promo, ...refinementListWithoutPromo } =
                    indexUiState.refinementList;
                if (Object.keys(refinementListWithoutPromo).length > 0) {
                    routeState.refinementList = refinementListWithoutPromo;
                }
            }

            // Range filters → flat keys (price_min, price_max, etc.)
            const range = (indexUiState as any).range || {};
            for (const { attribute, minKey, maxKey } of RANGE_META) {
                const r = range[attribute];
                if (!r) continue;
                if (minKey && r.min !== undefined && r.min !== "")
                    routeState[minKey] = Number(r.min);
                if (r.max !== undefined && r.max !== "")
                    routeState[maxKey] = Number(r.max);
            }

            // Page — serialize any page > 0 (page 0 = display page 1 = default, no param needed)
            if ((indexUiState as any).page > 0) {
                routeState.page = (indexUiState as any).page;
            }

            if (indexUiState.refinementList?.has_promotion?.includes("true")) {
                routeState.has_promotion = true;
            }

            return routeState;
        },

        routeToState(routeState: Record<string, any>): UiState {
            const menu = { ...(routeState.menu || {}) };
            const refinementList = { ...(routeState.refinementList || {}) };
            // Flat URL flag wins; drop nested legacy form if both are present.
            delete refinementList.has_promotion;

            // Legacy URLs used refinementList[make] after the brief multi-select
            // experiment; the make widget is ais-menu / connectMenu (menu[make]).
            const legacyMake = refinementList.make;
            if (
                Array.isArray(legacyMake) &&
                legacyMake.length > 0 &&
                !menu.make
            ) {
                menu.make = legacyMake[0];
                delete refinementList.make;
            }

            if (isHasPromotionRouteFlag(routeState.has_promotion)) {
                refinementList.has_promotion = ["true"];
            }

            const uiState: UiState = {
                [indexName]: {
                    query: routeState.query || "",
                    menu,
                    refinementList,
                },
            };

            // Sort
            if (routeState.sortBy) {
                (uiState[indexName] as any).sortBy = friendlyToSortIndex(
                    routeState.sortBy,
                    indexName,
                );
            }

            // Range filters from flat keys → InstantSearch range format
            const range: Record<string, { min?: number; max?: number }> = {};
            const filters = parseRouteStateFilters(routeState);
            for (const { attribute, minKey, maxKey } of RANGE_META) {
                const min = minKey
                    ? (filters[minKey] as number | undefined)
                    : undefined;
                const max = filters[maxKey] as number | undefined;
                if (min !== undefined || max !== undefined) {
                    range[attribute] = {
                        ...(min !== undefined ? { min } : {}),
                        ...(max !== undefined ? { max } : {}),
                    };
                }
            }
            if (Object.keys(range).length > 0) {
                (uiState[indexName] as any).range = range;
            }

            // Page
            if (routeState.page) {
                (uiState[indexName] as any).page = Number(routeState.page);
            }

            return uiState;
        },
    };
}

/** Flat `?has_promotion=` URL flag (true / "true" / "1"). */
export function isHasPromotionRouteFlag(value: unknown): boolean {
    return (
        value === true ||
        value === 1 ||
        value === "true" ||
        value === "1"
    );
}

function createBladeRouter() {
    return history({
        cleanUrlOnDispose: false,
        createURL({ qsModule, routeState, location }) {
            const queryString = qsModule.stringify(routeState, {
                addQueryPrefix: true,
                arrayFormat: "indices",
                encode: false,
            });
            return `${location.origin}${location.pathname}${queryString}`;
        },
        parseURL({ qsModule, location }): any {
            return qsModule.parse(location.search.slice(1), {
                arrayLimit: 100,
            });
        },
    });
}

/**
 * Routing factory for Blade marketplace pages.
 * Same URL format as the Vue version, but without Vue/Inertia dependencies.
 */
export const createAlgoliaRoutingBlade = (indexName: string) => ({
    router: createBladeRouter(),
    stateMapping: createBladeStateMapping(indexName),
});

/** 1-based display page from `?page=` on landing excerpt URLs. */
export function resolveLandingDisplayPageFromUrl(
    location: Pick<Location, "href"> = typeof window !== "undefined"
        ? window.location
        : { href: "" },
): number {
    const routeState = parseUrlToRouteState(location.href);
    const raw = routeState.page;

    if (raw === undefined || raw === null || raw === "") {
        return 1;
    }

    const displayPage = Number(raw);

    if (!Number.isFinite(displayPage) || displayPage < 1) {
        return 1;
    }

    return displayPage;
}

/**
 * Shareable pagination URL for landing / curated Blade pages.
 * `displayPage` is 1-based and matches the pagination widget labels.
 */
export function buildLandingPaginationUrl(
    location: Pick<Location, "href" | "pathname">,
    displayPage: number,
): string {
    const url = new URL(location.href);

    if (displayPage > 1) {
        url.searchParams.set("page", String(displayPage));
    } else {
        url.searchParams.delete("page");
    }

    return url
        .toString()
        .replace(/%5B/g, "[")
        .replace(/%5D/g, "]");
}

/** Landing excerpt URLs use 1-based `?page=`; InstantSearch state is 0-based. */
export function landingDisplayPageToInstantSearchPage(
    displayPage: number,
): number {
    return Math.max(0, displayPage - 1);
}

export function instantSearchPageToLandingDisplayPage(
    instantSearchPage: number,
): number {
    return instantSearchPage + 1;
}

export function isLandingExcerptPathname(pathname: string): boolean {
    return pathname !== "/marketplace" && pathname !== "";
}

/**
 * Landing excerpt URLs encode pagination as 1-based `?page=`; InstantSearch uses 0-based pages.
 * Applies to all non-/marketplace excerpt mounts (make/model/spec landings, CMS blocks), not only curated SEO pages.
 */
export function resolveInstantSearchPageFromRoutePage(
    rawPage: number,
    pathname: string,
): number {
    if (isLandingExcerptPathname(pathname)) {
        return landingDisplayPageToInstantSearchPage(rawPage);
    }

    return rawPage;
}
